/** Thin wrappers around the /auth/* endpoints - used by Login + bootstrap. */
import { api, apiGet, apiPost } from './api';
import { useAuthStore } from '../store/auth';

/**
 * Pull tokens out of an /auth/login or /auth/refresh response and push them
 * into the auth store. `force` controls how we treat the response's
 * `client_id`:
 *   - `force: true`  (initial login): the server's client_id is authoritative.
 *     If it's null (super-admin not mapped to a tenant), we explicitly clear
 *     activeClientId so a stale tenant from a previous account on this
 *     machine doesn't leak into the new session.
 *   - `force: false` (silent bootstrap-refresh on app start): only set
 *     activeClientId if the user hasn't already chosen one - preserves the
 *     tenant a super-admin had switched to before the page reload.
 */
function applyAuthPayload(data, { force = false } = {}) {
  const store = useAuthStore.getState();
  store.setTokens({
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
  });
  if (force) {
    store.setActiveClientId(data.client_id || null);
  } else if (!store.activeClientId && data.client_id) {
    store.setActiveClientId(data.client_id);
  }
}

export async function login({ email, password }) {
  // call directly so we don't accidentally trigger refresh-on-401 loops
  const r = await api.post('/auth/login', { email, password });
  applyAuthPayload(r.data, { force: true });
  // /auth/me, /me/permissions, /me/navigation are still needed for full
  // profile + sidebar - but every request from here on already carries the
  // correct X-Client-Id header.
  await refreshUserProfile();
  return useAuthStore.getState().user;
}

export async function logout({ everywhere = false } = {}) {
  const refresh = useAuthStore.getState().refreshToken;
  try {
    await apiPost('/auth/logout', { refresh_token: refresh, everywhere });
  } catch {
    // best-effort - local clear still happens below
  } finally {
    useAuthStore.getState().clear();
  }
}

export async function refreshUserProfile() {
  const me = await apiGet('/auth/me');
  useAuthStore.getState().setUser(me);
  const [perms, nav] = await Promise.all([
    apiGet('/auth/me/permissions'),
    apiGet('/auth/me/navigation'),
  ]);
  useAuthStore.getState().setPermissions(perms);
  useAuthStore.getState().setNavigation(nav);
  return me;
}

/**
 * Try to bootstrap a session from the persisted refresh token.
 * Returns true if successful, false if user must log in again.
 */
export async function bootstrapSession() {
  const { refreshToken, clear, setStatus } = useAuthStore.getState();
  if (!refreshToken) {
    setStatus('guest');
    return false;
  }
  setStatus('bootstrapping');
  try {
    const r = await api.post('/auth/refresh', { refresh_token: refreshToken });
    applyAuthPayload(r.data);
    await refreshUserProfile();
    setStatus('authed');
    return true;
  } catch {
    clear();
    return false;
  }
}
